import {
    configureCache,
    FeatureApiResponse,
    GrowthBook,
    setPolyfills,
} from '@growthbook/growthbook';
import { captureException, captureMessage } from '@sentry/nextjs';

import { FeatureToggleEnum } from '@nocowanie/core/enums/feature-toggle.enum';

import { environment } from '@app/environments/environment';

setPolyfills({
    fetch: (url: string, init: RequestInit) =>
        fetch(url, {
            ...init,
            next: {
                revalidate: environment.GROWTHBOOK.REVALIDATE_TIME,
                tags: [environment.GROWTHBOOK.REVALIDATE_TAG],
            },
        }),
});

configureCache({ disableCache: true });

export class GrowthbookService {
    static #instance: GrowthbookService;

    static get instance(): GrowthbookService {
        if (!GrowthbookService.#instance) {
            GrowthbookService.#instance = new GrowthbookService();
        }

        return GrowthbookService.#instance;
    }

    private reportInitFailure(context: string, source: string, error: Error | undefined): void {
        if (source === 'timeout') {
            captureMessage(
                `[GrowthBook] ${context} timed out; features will use defaults`,
                'warning',
            );
        } else {
            captureException(
                error ?? new Error(`[GrowthBook] ${context} failed (source: ${source})`),
            );
        }
    }

    async fetchPayload(): Promise<ReturnType<GrowthBook['getDecryptedPayload']>> {
        const gb = new GrowthBook({
            apiHost: environment.GROWTHBOOK.API_HOST,
            clientKey: environment.GROWTHBOOK.CLIENT_KEY,
        });

        try {
            const result = await gb.init({ timeout: environment.GROWTHBOOK.DEFAULT_TIMEOUT });

            if (!result.success) {
                this.reportInitFailure('fetchPayload', result.source, result.error);
            }

            return gb.getDecryptedPayload();
        } catch (error) {
            captureException(error);
            return {};
        } finally {
            gb.destroy();
        }
    }

    async isFeatureOn(featureKey: FeatureToggleEnum): Promise<boolean> {
        const gb = new GrowthBook({
            apiHost: environment.GROWTHBOOK.API_HOST,
            clientKey: environment.GROWTHBOOK.CLIENT_KEY,
        });

        try {
            const result = await gb.init({ timeout: environment.GROWTHBOOK.DEFAULT_TIMEOUT });

            if (!result.success) {
                this.reportInitFailure(`isFeatureOn(${featureKey})`, result.source, result.error);
            }

            return gb.isOn(featureKey);
        } catch (error) {
            captureException(error);
            return false;
        } finally {
            gb.destroy();
        }
    }

    async getFeatureValue<T>(featureKey: FeatureToggleEnum | string, defaultValue: T): Promise<T> {
        const gb = new GrowthBook({
            apiHost: environment.GROWTHBOOK.API_HOST,
            clientKey: environment.GROWTHBOOK.CLIENT_KEY,
        });

        try {
            const result = await gb.init({ timeout: environment.GROWTHBOOK.DEFAULT_TIMEOUT });

            if (!result.success) {
                this.reportInitFailure(
                    `getFeatureValue(${featureKey})`,
                    result.source,
                    result.error,
                );
            }

            return gb.getFeatureValue(featureKey, defaultValue) as T;
        } catch (error) {
            captureException(error);
            return defaultValue;
        } finally {
            gb.destroy();
        }
    }

    // Synchronously get feature flag from given payload
    getFeatureValueFromPayload<T>(
        payload: FeatureApiResponse,
        featureKey: FeatureToggleEnum | string,
        defaultValue: T,
    ): T {
        const gb = new GrowthBook({
            apiHost: environment.GROWTHBOOK.API_HOST,
            clientKey: environment.GROWTHBOOK.CLIENT_KEY,
        });

        try {
            gb.initSync({ payload });

            return gb.getFeatureValue(featureKey, defaultValue) as T;
        } catch (error) {
            captureException(error);
            return defaultValue;
        } finally {
            gb.destroy();
        }
    }
}
